import { useTranslation } from 'react-i18next';
import { useSnackbar } from '@c2fo/liquidity';
import { computeMarketSubmitStats } from '@/enterprise/components/OfferBuilder/utils';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import useToggleOffer, { ToggleOfferParams } from '@/enterprise/data/useToggleOffer';
import { useOfferModalContext } from '@/enterprise/features/offerModal/OfferModalContext';
import { useReporting } from '@/reporting';
import { EnterpriseTrackEventNames } from '@/reporting/trackEventNames';
import useExchangeRates from '@/shared/data/useExchangeRates';

/**
 * Reusable hook to turn off participation for a market
 * Handles event tracking, error handling, and success feedback
 */
export const useTurnOffParticipation = (takerMarket: TakerMarket) => {
  const { t } = useTranslation();
  const showSnackbar = useSnackbar();
  const { trackEnterpriseEvent } = useReporting();
  const { closeModal } = useOfferModalContext();
  const { mutateAsync: toggleOffer, isPending: toggleOfferIsPending } = useToggleOffer();
  const { data: exchangeRates } = useExchangeRates();

  const handleToggleSuccess = (submitValues: ToggleOfferParams, eventType: EnterpriseTrackEventNames) => {
    showSnackbar({ message: t('offerSubmitDialog.participationUpdated') });

    const marketStats = computeMarketSubmitStats({
      takerMarket,
      rate: 0,
      rateType: takerMarket.offerConfig.isDiscountBidding ? 'DISC' : 'APR',
      exchangeRates,
    });

    trackEnterpriseEvent(eventType, {
      ...submitValues,
      offerConfig: {
        ...takerMarket.offerConfig,
        isEnabled: false,
      },
      awardedInvoiceAmount: takerMarket.offerConfig.isManuallyAwarded ? takerMarket.awardedInvoiceAmount : undefined,
      isDraft: false,
      isEdit: false,
      isEnabled: false,
      isPreferred: takerMarket.offerConfig.offerType === 'PREFERRED_TERM',
      ...marketStats,
    });

    closeModal();
  };

  const handleToggleOffer = async (submitValues: ToggleOfferParams, eventType: EnterpriseTrackEventNames) => {
    return await toggleOffer(submitValues, {
      onSuccess: () => handleToggleSuccess(submitValues, eventType),
      onError: () => showSnackbar({ message: t('offerSubmitDialog.participationError') }),
    });
  };

  const turnOffParticipation = async () => {
    // Only proceed if participation is currently enabled
    if (!takerMarket.offerConfig.isEnabled) {
      return;
    }

    const submitValues = {
      marketUuid: takerMarket.offerConfig.marketUuid,
      supplierDivisionUuid: takerMarket.takerDivisionUuid,
      seaUuid: takerMarket.offerConfig.uuid ?? undefined,
      frequency: takerMarket.offerConfig.frequency,
      makerOrganizationUuid: takerMarket.makerOrganizationUuid,
      marketPricingType: takerMarket.marketPricingType ?? undefined,
      isManuallyAwarded: takerMarket.offerConfig.isManuallyAwarded,
      isActiveExpressAccept: takerMarket.offerConfig.isActiveExpressAccept,
    } satisfies ToggleOfferParams;

    let eventType: EnterpriseTrackEventNames = 'participation::submitted';

    if (takerMarket.offerConfig.isActiveExpressAccept) {
      eventType = 'participation::express-accept::disabled';
    } else if (takerMarket.offerConfig.isManuallyAwarded) {
      eventType = 'participation::manually-awarded::disabled';
    } else {
      eventType =
        takerMarket.marketPricingType === 'BENCHMARK'
          ? 'participation::variable-rate::submitted'
          : takerMarket.marketPricingType === 'STATIC'
          ? 'participation::fixed-rate::submitted'
          : 'participation::submitted';
    }

    return await handleToggleOffer(submitValues, eventType);
  };

  return {
    turnOffParticipation,
    isLoading: toggleOfferIsPending,
  };
};
